import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generateAudioAssetService, getAudioAssetsService, deleteAudioAssetService, getFeaturedAudioAssetsService } from '../../services/audio_assets';

// Generate audio asset (unified for sound effects & music)
interface IGenerateAudioAssetParams {
    org_id: string;
    integration_id: string;
    type: 'sound-effect' | 'music';
    prompt: string;
    socketConnectionId: string;
    // Sound effect params
    seconds_total?: number;
    // Music params
    duration?: number;
    instrumental?: boolean;
}

interface IAudioAssetResponse {
    async?: boolean;
    status?: string;
    data?: {
        audio_url: string;
        duration: number;
        prompt: string;
        fileSize: number;
        tags?: string;
    };
}

export interface IAudioAsset {
    id: string;
    integration_id: string;
    prompt: string;
    audio_url: string;
    duration: number;
    file_size: number;
    category?: string;
    tags?: string[];
    is_public?: boolean;
    is_featured?: boolean;
    status: string;
    created_at: string;
    updated_at: string;
}


interface IPaginatedResponse<T> {
    data: T[];
    count: number;
    next_page_token: string | null;
}

interface IGetAudioAssetsParams {
    org_id: string;
    integration_id: string;
    type?: 'sound-effect' | 'music';
    limit?: number;
    next_page_token?: string | null;
    search?: string;
}

interface IDeleteAudioAssetParams {
    org_id: string;
    integration_id: string;
    asset_id: string;
    type?: 'sound-effect' | 'music';
}

/**
 * Unified hook for generating audio assets (sound effects & music)
 */
export const useGenerateAudioAsset = () => {
    return useMutation(
        async ({ 
            org_id, 
            integration_id,
            type,
            prompt,
            socketConnectionId,
            seconds_total,
            duration,
            instrumental
        }: IGenerateAudioAssetParams) => {
            const response = await generateAudioAssetService({
                org_id,
                integration_id,
                type,
                prompt,
                socketConnectionId,
                seconds_total,
                duration,
                instrumental
            });
            return response as IAudioAssetResponse;
        }
    );
};

export const useGetAudioAssets = ({ 
    org_id, 
    integration_id, 
    type = 'sound-effect',
    limit = 20,
    next_page_token = null,
    search = ''
}: IGetAudioAssetsParams) => {
    return useQuery(
        ['audio-assets', type, org_id, integration_id, limit, next_page_token, search],
        async () => {
            const response: any = await getAudioAssetsService({ 
                org_id, 
                integration_id, 
                type,
                limit,
                next_page_token: next_page_token || undefined,
                search: search || undefined
            });
            return {
                data: (response.data || []) as IAudioAsset[],
                count: response.count || 0,
                next_page_token: response.next_page_token || null
            } as IPaginatedResponse<IAudioAsset>;
        },
        {
            enabled: !!org_id && !!integration_id,
            staleTime: 30000, // 30 seconds
            keepPreviousData: true // Keep previous data while fetching next page
        }
    );
};

export const useDeleteAudioAsset = () => {
    const queryClient = useQueryClient();
    
    return useMutation(
        async ({ org_id, integration_id, asset_id, type = 'sound-effect' }: IDeleteAudioAssetParams) => {
            const response = await deleteAudioAssetService({
                org_id,
                integration_id,
                asset_id,
                type
            });
            return response;
        },
        {
            onSuccess: (_, variables) => {
                // Invalidate all pages of audio assets list to refetch
                const assetType = variables.type || 'sound-effect';
                queryClient.invalidateQueries(['audio-assets', assetType, variables.org_id, variables.integration_id]);
            }
        }
    );
};

interface IGetFeaturedAudioAssetsParams {
    type: 'sound-effect' | 'music';
    limit?: number;
    next_page_token?: string | null;
    search?: string;
}

export const useGetFeaturedAudioAssets = ({ 
    type,
    limit = 20,
    next_page_token = null,
    search = ''
}: IGetFeaturedAudioAssetsParams) => {
    return useQuery(
        ['audio-assets', 'featured', type, limit, next_page_token, search],
        async () => {
            const response: any = await getFeaturedAudioAssetsService({ 
                type,
                limit,
                next_page_token: next_page_token || undefined,
                search: search || undefined
            });
            return {
                data: (response.data || []) as IAudioAsset[],
                count: response.count || 0,
                next_page_token: response.next_page_token || null
            } as IPaginatedResponse<IAudioAsset>;
        },
        {
            enabled: !!type,
            staleTime: 5 * 60 * 1000, // 5 minutes (featured content changes less frequently)
            keepPreviousData: true // Keep previous data while fetching next page
        }
    );
};
